import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function TibaGlobalAwardFiveYearsPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'TIBA 5年連続グローバルアワード受賞' }
  ];

  const article = {
    category: '教育・業界権威',
    date: '2024年12月20日',
    title: 'TIBA 5年連続グローバルアワード受賞',
    subtitle: '世界最高水準の執事訓練機関として認定',
    heroImage: '/images/generated/jba-fs-132-news_tiba-global-award-five-years.webp',
    lead: 'オランダを拠点とするThe International Butler Academy（TIBA）が、Corporate Livewire Global Awardsにて5年連続で最優秀バトラー訓練機関として認定されました。高い卒業率と就職率を誇るTIBAの成功は、執事教育の国際的標準化と専門教育の価値を示す重要な事例です。',
    comment: 'The International Butler Academy（TIBA）の5年連続グローバルアワード受賞は、執事教育の国際的標準化における重要なマイルストーンとして、弊協会としても深く敬意を表しております。高い卒業率と就職率が証明する専門教育の価値は、執事という職業の専門性と経済的価値を世界に示す重要な指標となっています。\n\nグローバルな執事人材育成における教育機関の役割は、単なる技術習得を超えて、サービスの芸術としての執事文化の継承と発展に寄与するものです。\n\nTIBAの継続的な成功は、執事教育が確立された学問分野として認識されていることを示しており、日本における執事教育の発展可能性についても大きな示唆を与えています。私どもとしても、国際的な教育標準に準拠しながら、日本独自の「おもてなし」文化と融合した執事教育プログラムの確立に向けて、より一層の努力を重ねてまいります。',
    related: [
      { to: '/news/grant-harrold-royal-butler-memoir', image: '/images/generated/jba-fs-137-news_tiba-global-award-five-years-v4.webp', aspect: 'aspect-[3/4]', alt: 'グラント・ハロルド王室バトラー回顧録出版の関連記事', title: 'グラント・ハロルド王室バトラー回顧録出版', desc: 'チャールズ国王の元バトラーが語る王室での経験' },
      { to: '/news/raffles-butler-campaign', image: '/images/generated/jba-fs-138-news_tiba-global-award-five-years-v4.webp', aspect: 'aspect-[3/4]', alt: 'Raffles「The Butler Did It」グローバルキャンペーンの関連記事', title: 'Raffles「The Butler Did It」グローバルキャンペーン', desc: 'ラグジュアリーホテルの執事サービス革新' }
    ]
  };

  const sources = [
    { label: '出典元', value: 'The International Butler Academy 公式サイト', link: false },
    { label: '公式サイト', value: 'https://www.butlerschool.com/', link: true },
    { label: '就職・実績', value: 'https://www.butlerschool.com/recruitment-agency/finding-work-and-internships/', link: true },
    { label: '受賞（CFI）', value: 'https://cfi.co/awards/europe/2020/the-international-butler-academy-best-private-butler-training-global-2020/', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];

  const toc = [
    { id: 's-org', label: '組織概要と受賞歴' },
    { id: 's-accolades', label: '世界的な受賞歴の詳細' },
    { id: 's-program', label: '訓練プログラムの特徴と成果' },
    { id: 's-philosophy', label: '教育理念と変革的学習体験' },
    { id: 's-network', label: '卒業生の進路とネットワーク' },
    { id: 's-global', label: 'グローバル展開と業界への影響' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Education" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'TIBAはCapital Finance Internationalから2019・2020年に「世界最高の執事学校」、Corporate Livewireから2021〜2025年まで連続でグローバルアワードを受賞している',
                '卒業率95％・就職率85％。卒業生は王室邸宅、プライベートホーム、ホテル、大使館、クルーズ船、スーパーヨット、Fortune 500企業などで活躍している',
                '1999年にRobert Wennekesがオランダ・シンペルフェルトで創立。900点以上を獲得した卒業生には就職を保証している'
              ]}
            />

            {/* 01: 組織概要と受賞歴（既存本文・一字も削らず） */}
            <SectionHeading id="s-org" en="Overview">組織概要と受賞歴</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The International Butler Academy（TIBA）は、オランダを拠点とする世界最高水準の執事訓練機関です。「世界で最高の執事学校へようこそ」をモットーに、卓越したサービスの専門家を育成しています。</p>
              <p className={P}>TIBAは、Corporate Livewire Global Awardsにて5年連続（2021年〜2025年）で最優秀バトラー訓練機関として認定されており、さらにCapital Finance Internationalからも「世界最高のバトラー訓練機関」として認定されています。この継続的な受賞は、業界における確固たる地位を確立していることを示しています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/tiba-global-award-five-years-butlerschool.jpg"
              alt="The International Butler Academy（TIBA）公式サイトのトップページのスクリーンショット。受賞歴とモットーが掲載されている"
              media="The International Butler Academy 公式サイト"
              url="https://www.butlerschool.com/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="世界で最高の執事学校へようこそ。卓越したサービスの世界へ。" cite="The International Butler Academy" />
            </div>

            {/* 増補: 受賞歴の詳細 */}
            <SectionHeading id="s-accolades" en="Global Accolades">世界的な受賞歴の詳細</SectionHeading>
            <div className="space-y-6">
              <p className={P}>TIBAはCapital Finance Internationalから「世界最高の執事学校（The Best Butler School in the World）」として<Stat>2019</Stat>年および<Stat>2020</Stat>年に表彰されました。さらにCorporate Livewireからは「Specialist Education & Training Provider of the Year」のグローバルアワードを、2021年から2025年まで<Stat>5</Stat>年連続で受賞しています。</p>
              <p className={P}>これらの独立した第三者機関による継続的な評価は、TIBAが単発ではなく長期にわたり業界標準を維持していることの証左となっています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/tiba-global-award-five-years-cfi.jpg"
              alt="Capital Finance International（CFI.co）の受賞ページのスクリーンショット。The International Butler Academyの受賞が掲載されている"
              media="CFI.co（Capital Finance International）"
              url="https://cfi.co/awards/europe/2020/the-international-butler-academy-best-private-butler-training-global-2020/"
            />

            {/* 02: プログラム（既存本文・一字も削らず） */}
            <SectionHeading id="s-program" en="Program">訓練プログラムの特徴と成果</SectionHeading>
            <div className="space-y-6">
              <p className={P}>TIBAの訓練プログラムは8週間の高度集中訓練として設計されており、非常に高い成果を上げています。卒業率は95%、就職率は85%という優秀な実績を誇り、卒業生の平均年収も高水準に達しています。</p>
              <p className={P}>カリキュラムには、クラシックなバトラーサービス技術、ワインサービスとソムリエスキル、テーブルセッティングとダイニングエチケット、国際的なエチケットとプロトコル、現代的な家庭管理スキル、高級ホスピタリティサービスが含まれています。</p>
            </div>

            {/* 03: 教育理念（既存本文・一字も削らず） */}
            <SectionHeading id="s-philosophy" en="Philosophy">教育理念と変革的学習体験</SectionHeading>
            <div className="space-y-6">
              <p className={P}>TIBAは「スタイル、エレガンス、礼儀正しさの世界へようこそ。卓越したサービスの世界へ。ここは執事のための学校であり、夢が羽ばたき、プロフェッショナリズムが育まれる場所です」という理念を掲げています。</p>
              <p className={P}>すべての細部が重要視され、エチケットと精密さが絡み合い、真のサービスの芸術がライフスタイルとなる変革的な旅を提供しています。卒業生は名誉ある機会への扉を開き、執事の世界に永続的な遺産を築く準備が整います。</p>
            </div>

            {/* 増補: 卒業生の進路とネットワーク */}
            <SectionHeading id="s-network" en="Graduate Outcomes">卒業生の進路とネットワーク</SectionHeading>
            <div className="space-y-6">
              <p className={P}>卒業生の<Stat>95</Stat>％がディプロマを取得し、<Stat>85</Stat>％が王室邸宅やプライベートホーム、ホテル、大使館、クルーズ船、スーパーヨット、Fortune 500企業といった一流の職場で活躍しています。</p>
              <p className={P}>TIBAはオランダ・シンペルフェルトの拠点を中心に、世界中の著名雇用主との広範なネットワークを築いており、これが高品質な人材を継続的に輩出する基盤となっています。</p>
            </div>

            {/* 04: グローバル展開（既存本文・一字も削らず） */}
            <SectionHeading id="s-global" en="Global Reach">グローバル展開と業界への影響</SectionHeading>
            <div className="space-y-6">
              <p className={P}>TIBAは国際的な認知度を持ち、世界中から学生を受け入れています。卒業生は、ヨーロッパ、中東、アジア、アメリカなど、世界各地の高級ホテル、プライベートレジデンス、スーパーヨット、王室などで活躍しています。</p>
              <p className={P}>5年連続での受賞は、TIBAが業界標準を確立し続けていることを示しています。この継続的な認定は、執事訓練を検討している候補者にとって重要な選択基準となり、TIBAのブランド価値をさらに高めています。高い卒業率と就職率は、TIBAのプログラムの質の高さと、卒業生が業界で即戦力として評価されていることを証明しています。</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">拠点</dt><dd className="text-[#333] mt-0.5">オランダ・シンペルフェルト</dd></div>
                  <div><dt className="text-[#8f7a56]">創立</dt><dd className="text-[#333] mt-0.5">1999年（Robert Wennekes）</dd></div>
                  <div><dt className="text-[#8f7a56]">受賞</dt><dd className="text-[#333] mt-0.5">5年連続（2021-2025）</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
